// Servizio per la gestione delle unità eroganti

import apiClient from './apiClient';
import { ExecutionUnit, ApiResponse } from '../types';
import { Doctor, HealthcareService } from '../types/executionUnit';

// Interfaccia per le opzioni di cancellazione
export interface DeleteOptions {
    type: 'soft' | 'hard';
    reason?: string;
    isHardDelete: boolean;
}

// Risultato della cancellazione
export interface DeleteResult {
    success: boolean;
    message: string;
    details?: {
        local_deleted: boolean;
        recup_deleted: boolean;
        recup_id?: string | null;
    };
    note?: string;
}

// Funzione per recuperare tutte le unità eroganti
export const getExecutionUnits = async (): Promise<ExecutionUnit[]> => {
    try {
        const response = await apiClient.get('/integration/execution-units');
        return (response.data?.data || []) as ExecutionUnit[];
    } catch (error) {
        console.error('Errore durante il recupero delle unità eroganti:', error);
        return [] as ExecutionUnit[];
    }
};

// Funzione per sincronizzare le unità eroganti da HealthNET
export const syncExecutionUnits = async () => {
    try {
        const response = await apiClient.post('/integration/execution-units/sync');
        return response.data?.data || {};
    } catch (error) {
        console.error('Errore durante la sincronizzazione delle unità eroganti:', error);
        throw error;
    }
};

// Funzione per creare una nuova unità erogante
export const createExecutionUnit = async (executionUnit: Omit<ExecutionUnit, 'id' | 'createdAt' | 'updatedAt'>): Promise<ExecutionUnit> => {
    try {
        const response = await apiClient.post('/execution-units', executionUnit);
        return (response.data?.data || {
            id: Date.now().toString(),
            ...executionUnit,
            createdAt: new Date(),
            updatedAt: new Date()
        }) as ExecutionUnit;
    } catch (error) {
        console.error('Errore durante la creazione dell\'unità erogante:', error);
        throw error;
    }
};

// Funzione per aggiornare un'unità erogante
export const updateExecutionUnit = async (id: string, executionUnit: Partial<ExecutionUnit>): Promise<ExecutionUnit> => {
    try {
        const response = await apiClient.put(`/execution-units/${id}`, executionUnit);
        return (response.data?.data || { id, ...executionUnit, updatedAt: new Date() }) as ExecutionUnit;
    } catch (error) {
        console.error('Errore durante l\'aggiornamento dell\'unità erogante:', error);
        throw error;
    }
};

// Funzione per eliminare un'unità erogante (soft o hard delete)
export const deleteExecutionUnit = async (id: string, options: DeleteOptions): Promise<DeleteResult> => {
    try {
        const params = new URLSearchParams();
        if (options.isHardDelete) {
            params.append('hard_delete', 'true');
        }

        const response = await apiClient.delete<DeleteResult>(`/execution-units/${id}?${params.toString()}`, {
            data: options.reason ? { reason: options.reason } : undefined
        });
        
        return response.data;
    } catch (error) {
        console.error('Errore durante l\'eliminazione dell\'unità erogante:', error);
        throw error;
    }
};

// Backward compatibility: funzione legacy per soft delete
export const deleteExecutionUnitLegacy = async (id: string, reason?: string): Promise<void> => {
    await deleteExecutionUnit(id, {
        type: 'soft',
        reason,
        isHardDelete: false
    });
};

// Funzione per ripristinare un'unità erogante eliminata
export const restoreExecutionUnit = async (id: string): Promise<void> => {
    try {
        await apiClient.post(`/execution-units/${id}/restore`);
    } catch (error) {
        console.error('Errore durante il ripristino dell\'unità erogante:', error);
        throw error;
    }
};

// Funzione per ottenere unità eroganti eliminate
export const getDeletedExecutionUnits = async (): Promise<ExecutionUnit[]> => {
    try {
        const response = await apiClient.get<ApiResponse<ExecutionUnit[]>>('/execution-units/deleted/list');
        if (response.data && 'data' in response.data) {
            return response.data.data || [];
        }
        return [];
    } catch (error) {
        console.error('Errore durante il recupero delle unità eroganti eliminate:', error);
        return [];
    }
};

// Funzione per ottenere audit trail di un'unità erogante
export const getExecutionUnitAuditTrail = async (id: string): Promise<any[]> => {
    try {
        const response = await apiClient.get<ApiResponse<any[]>>(`/execution-units/${id}/audit`);
        if (response.data && 'data' in response.data) {
            return response.data.data || [];
        }
        return [];
    } catch (error) {
        console.error('Errore durante il recupero dell\'audit trail:', error);
        return [];
    }
};

// === GESTIONE ASSOCIAZIONI DOCTORS E SERVICES ===

// Recupera i medici associati a una unità erogante
export const getExecutionUnitDoctors = async (unitId: string): Promise<Doctor[]> => {
    try {
        const response = await apiClient.get(`/execution-units/${unitId}/doctors`);
        return (response.data?.data || []) as Doctor[];
    } catch (error) {
        console.error('Errore durante il recupero dei medici della UE:', error);
        return [];
    }
};

// Recupera i servizi associati a una unità erogante
export const getExecutionUnitServices = async (unitId: string): Promise<HealthcareService[]> => {
    try {
        const response = await apiClient.get(`/execution-units/${unitId}/services`);
        return (response.data?.data || []) as HealthcareService[];
    } catch (error) {
        console.error('Errore durante il recupero dei servizi della UE:', error);
        return [];
    }
};

// Aggiorna l'associazione medici di una unità erogante
export const updateExecutionUnitDoctors = async (unitId: string, doctorIds: string[]): Promise<void> => {
    try {
        await apiClient.put(`/execution-units/${unitId}/doctors`, { doctorIds });
    } catch (error) {
        console.error('Errore durante l\'aggiornamento dei medici della UE:', error);
        throw error;
    }
};

// Aggiorna l'associazione servizi di una unità erogante
export const updateExecutionUnitServices = async (unitId: string, serviceIds: string[]): Promise<void> => {
    try {
        await apiClient.put(`/execution-units/${unitId}/services`, { serviceIds });
    } catch (error) {
        console.error('Errore durante l\'aggiornamento dei servizi della UE:', error);
        throw error;
    }
};

// Recupera tutti i medici disponibili per l'associazione
export const getAllAvailableDoctors = async (): Promise<Doctor[]> => {
    try {
        const response = await apiClient.get('/doctors');
        return (response.data?.data || []) as Doctor[];
    } catch (error) {
        console.error('Errore durante il recupero di tutti i medici:', error);
        return [];
    }
};

// Recupera tutti i servizi sanitari disponibili per l'associazione
export const getAllAvailableServices = async (): Promise<HealthcareService[]> => {
    try {
        const response = await apiClient.get('/healthcare-services');
        return (response.data?.data || []) as HealthcareService[];
    } catch (error) {
        console.error('Errore durante il recupero di tutti i servizi:', error);
        return [];
    }
};

// Oggetto servizio di default per compatibilità
const executionUnitService = {
    getExecutionUnits,
    syncExecutionUnits,
    createExecutionUnit,
    updateExecutionUnit,
    deleteExecutionUnit,
    deleteExecutionUnitLegacy,
    restoreExecutionUnit,
    getDeletedExecutionUnits,
    getExecutionUnitAuditTrail,
    // Nuove funzioni per associazioni
    getExecutionUnitDoctors,
    getExecutionUnitServices,
    updateExecutionUnitDoctors,
    updateExecutionUnitServices,
    getAllAvailableDoctors,
    getAllAvailableServices
};

export { executionUnitService };
export default executionUnitService;
